import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Html5QrcodeScanner } from "html5-qrcode";
import { Camera, ScanLine, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

const QRScan = () => {
  const [active, setActive] = useState(false);
  const [date, setDate] = useState(today());
  const [status, setStatus] = useState("present");
  const [recent, setRecent] = useState([]);
  const [photoMode, setPhotoMode] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scannerRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [manualPick, setManualPick] = useState("");

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  // QR Scanner
  useEffect(() => {
    if (!active) return;
    const scanner = new Html5QrcodeScanner(
      "qr-reader",
      { fps: 10, qrbox: { width: 240, height: 240 } },
      false,
    );
    scannerRef.current = scanner;
    scanner.render(async (decoded) => {
      try {
        const { data } = await api.post("/id-cards/scan/resolve", { value: decoded });
        if (data.person_type !== "student") return toast.error("This is not a student attendance card");
        await markAtt(data.person_id, "qr");
      } catch (e) { toast.error(formatApiError(e)); }
    }, () => {});
    return () => { try { scanner.clear(); } catch (e) {} };
  }, [active, students, status, date]);

  const markAtt = async (sid, method) => {
    try {
      let photo = null;
      if (method === "photo" && videoRef.current) {
        const canvas = canvasRef.current;
        canvas.width = videoRef.current.videoWidth; canvas.height = videoRef.current.videoHeight;
        canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
        photo = canvas.toDataURL("image/jpeg", 0.6);
      }
      const { data } = await api.post("/attendance/mark", {
        subject_id: sid, subject_type: "student", date, status, method, photo_data: photo,
      });
      const stu = students.find((s) => s.id === sid);
      toast.success(`Marked ${stu?.full_name} ${status}`);
      setRecent((r) => [{ ...data, name: stu?.full_name, adm: stu?.admission_no }, ...r].slice(0, 20));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      videoRef.current.srcObject = stream;
      setPhotoMode(true);
    } catch (e) {
      toast.error("Camera permission required for photo capture.");
    }
  };

  const stopCamera = () => {
    const s = videoRef.current?.srcObject;
    if (s) s.getTracks().forEach((t) => t.stop());
    setPhotoMode(false);
  };

  return (
    <div data-testid="qr-scan-page">
      <PageHeader
        overline="Biometric Attendance"
        title="QR Scanner & Photo Capture"
        description="Scan a student's QR ID card to instantly mark attendance, or take a photo for verified manual marking."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-10 bg-white" data-testid="qr-date" />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36 h-10 bg-white" data-testid="qr-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="late">Late</SelectItem>
              </SelectContent>
            </Select>
            {!active ? (
              <Button onClick={() => setActive(true)} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="qr-start">
                <ScanLine className="w-4 h-4 mr-2" /> Start QR scanner
              </Button>
            ) : (
              <Button variant="outline" onClick={() => { try { scannerRef.current?.clear(); } catch (e) {} setActive(false); }} data-testid="qr-stop">
                <RefreshCcw className="w-4 h-4 mr-2" /> Stop
              </Button>
            )}
            {!photoMode ? (
              <Button variant="outline" onClick={startCamera} data-testid="qr-start-camera">
                <Camera className="w-4 h-4 mr-2" /> Start photo capture
              </Button>
            ) : (
              <Button variant="outline" onClick={stopCamera}>Stop camera</Button>
            )}
          </div>

          {active && <div id="qr-reader" className="rounded-lg overflow-hidden bg-black/5" />}

          {photoMode && (
            <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-4 items-start">
              <video ref={videoRef} autoPlay playsInline muted className="rounded-lg bg-black w-full max-h-72 object-cover" />
              <div className="space-y-2">
                <Select value={manualPick} onValueChange={setManualPick}>
                  <SelectTrigger className="w-56 h-10 bg-white"><SelectValue placeholder="Select student" /></SelectTrigger>
                  <SelectContent>
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.admission_no}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  disabled={!manualPick}
                  onClick={() => markAtt(manualPick, "photo")}
                  className="w-56 bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]"
                  data-testid="qr-photo-capture"
                >Capture & Mark</Button>
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {!active && !photoMode && (
            <div className="text-[#475569] text-sm p-8 text-center border border-dashed border-[#E2E8F0] rounded-lg">
              New secure ID cards and existing <code className="font-mono text-xs">STUDENT:&lt;admission_no&gt;</code> cards are supported.
            </div>
          )}
        </Card>

        <Card className="p-5 border-[#E2E8F0]">
          <div className="overline text-[#9DCD3F] mb-3">Just Now</div>
          {recent.length === 0 ? (
            <div className="text-sm text-[#475569]">No scans yet today.</div>
          ) : (
            <ul className="space-y-3" data-testid="qr-recent">
              {recent.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <div className="w-9 h-9 rounded-full bg-[#1B2C5A]/5 text-[#1B2C5A] grid place-items-center text-xs font-medium">{r.name?.split(" ").map(x => x[0]).slice(0, 2).join("")}</div>
                  <div>
                    <div className="text-[#1B2C5A] font-medium">{r.name}</div>
                    <div className="text-xs text-[#475569]">{r.adm} · {r.status} · {r.method}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default QRScan;
